/**
 * Legal EYFS ratio compliance engine (BATCH A).
 *
 * Statutory ratios:
 *   under_2          1 : 3
 *   two_year_olds    1 : 4
 *   three_to_five    1 : 8  (1 : 13 when an EYT/EYPS/QTS lead is on duty)
 *   out_of_school    1 : 8
 *   mixed            Most restrictive of ages actually present.
 */

// Single source of truth for the EYFS ratio rules. Used by the browser
// (via src/lib/ratioEngine.ts) and by the server check (cron-ratio-check), so
// both always agree. Keep this file free of app-only imports.
/* eslint-disable @typescript-eslint/no-explicit-any */
type DbChild = { id: string; dob: string; [k: string]: any };
type DbZone = { id: string; name: string; [k: string]: any };
type DbShift = { id: string; staff_id: string; zone_id: string | null; [k: string]: any };
type DbStaff = { id: string; [k: string]: any };
export type AgeGroup =
  | "under_2"
  | "two_year_olds"
  | "three_to_five"
  | "mixed"
  | "out_of_school";

export type DutyStatus = "on_duty" | "on_break" | "in_meeting" | "admin_duties";

export type RatioStatus = "at_ratio" | "approaching" | "breached";
export type BreachType = "ratio" | "unqualified_unsupervised" | "eyt_lost" | "pfa_lost";

export interface ZoneCompliance {
  zoneId: string;
  zoneName: string;
  ageGroup: AgeGroup;
  isEytLed: boolean;
  legalRatio: string;            // "1:8"
  requiredAdults: number;
  countedStaff: number;          // qualified on-duty + supervised unqualified
  qualifiedOnDuty: number;
  unqualifiedOnDuty: number;
  signedInChildren: number;
  currentRatioLabel: string;     // e.g. "1:3.7" or "N/A"
  status: RatioStatus;
  breachTypes: BreachType[];
  message: string;
  /** Exact inputs used, stored with any alert so it can be traced later. */
  snapshot: Record<string, unknown>;
}

const DEFAULT_DENOM: Record<AgeGroup, number> = {
  under_2: 3,
  two_year_olds: 4,
  three_to_five: 8,
  out_of_school: 8,
  mixed: 3, // fallback, recomputed via ages
};

export function getLegalRatioDenominator(group: AgeGroup, isEytLed: boolean): number {
  if (group === "three_to_five" && isEytLed) return 13;
  return DEFAULT_DENOM[group];
}

export function ageInYears(dob: string, now = new Date()): number {
  const d = new Date(dob);
  const months =
    (now.getFullYear() - d.getFullYear()) * 12 +
    (now.getMonth() - d.getMonth()) -
    (now.getDate() < d.getDate() ? 1 : 0);
  return months / 12;
}

export function ageGroupForChild(dob: string, now = new Date()): AgeGroup {
  const yrs = ageInYears(dob, now);
  if (yrs < 2) return "under_2";
  if (yrs < 3) return "two_year_olds";
  return "three_to_five";
}

/** Most restrictive denominator across ages present (used for mixed zones). */
function mostRestrictiveDenominator(
  children: DbChild[],
  isEytLed: boolean,
  now: Date,
): { denom: number; group: AgeGroup } {
  if (children.length === 0) return { denom: 8, group: "three_to_five" };
  let bestDenom = 999;
  let bestGroup: AgeGroup = "three_to_five";
  for (const c of children) {
    const g = ageGroupForChild(c.dob, now);
    const d = getLegalRatioDenominator(g, isEytLed);
    if (d < bestDenom) {
      bestDenom = d;
      bestGroup = g;
    }
  }
  return { denom: bestDenom, group: bestGroup };
}

interface ComputeArgs {
  zone: DbZone & { primary_age_group?: string | null };
  signedInChildren: DbChild[];        // currently signed in to this zone
  activeShifts: (DbShift & {
    duty_status?: string | null;
    is_eyt_led_session?: boolean | null;
  })[];                                // shifts overlapping `now`
  staffList: (DbStaff & {
    is_qualified_l3?: boolean | null;
    has_eyt_eyps?: boolean | null;
    paediatric_first_aid?: boolean | null;
    first_aid_expiry?: string | null;
  })[];
  now?: Date;
  /** Staff signed in at the Kiosk right now whose shift has no room set. */
  unassignedStaffSignedIn?: number;
}

export function computeZoneCompliance(args: ComputeArgs): ZoneCompliance {
  const now = args.now ?? new Date();
  const { zone, signedInChildren, activeShifts, staffList } = args;
  const zoneId = zone.id;

  const configured = (zone.primary_age_group as AgeGroup | undefined) || inferAgeGroup(zoneId);

  // Shifts in this zone that are currently "on duty"
  const zoneShifts = activeShifts.filter(
    (s) =>
      s.zone_id === zoneId &&
      (s.duty_status ?? "on_duty") === "on_duty",
  );

  // Staff lookups
  const byId = new Map(staffList.map((s) => [s.id, s]));

  // Is there a qualified EYT/EYPS/QTS leader on duty AND a shift flagged eyt-led?
  const eytLedShift = zoneShifts.find((s) => s.is_eyt_led_session);
  const eytStaff = eytLedShift ? byId.get(eytLedShift.staff_id) : undefined;
  const isEytLed = !!(eytLedShift && eytStaff?.has_eyt_eyps);
  const eytLost = !!eytLedShift && !isEytLed; // toggle set but qualifier not on duty

  // Resolve age group + denominator
  let ageGroup: AgeGroup = configured;
  let denom: number;
  if (configured === "mixed") {
    const r = mostRestrictiveDenominator(signedInChildren, isEytLed, now);
    denom = r.denom;
    ageGroup = "mixed";
  } else {
    denom = getLegalRatioDenominator(configured, isEytLed);
  }

  const legalRatio = `1:${denom}`;

  // Each person counts once, even with split/duplicate shifts.
  // Archived staff never count, even if an old planned shift still exists.
  const zoneStaffIds = Array.from(new Set(zoneShifts.map((s) => s.staff_id))).filter((id) => {
    if (byId.size === 0) return true;
    const st = byId.get(id) as { is_active?: boolean | null } | undefined;
    return !!st && st.is_active !== false;
  });
  const qualifiedOnDuty = zoneStaffIds.filter(
    (id) => (byId.get(id)?.is_qualified_l3 ?? true) === true,
  ).length;
  const unqualifiedOnDuty = zoneStaffIds.length - qualifiedOnDuty;

  // Ratio headcount depends only on who is present in the zone. Qualification
  // (L3, PFA, DBS) is checked by its own separate rules below.
  const countedStaff = zoneStaffIds.length;

  const childCount = signedInChildren.length;
  const requiredAdults = Math.ceil(childCount / denom);
  const unassigned = args.unassignedStaffSignedIn ?? 0;

  const breachTypes: BreachType[] = [];
  let status: RatioStatus = "at_ratio";

  // Separate rule: unqualified staff with no qualified colleague present.
  if (unqualifiedOnDuty > 0 && qualifiedOnDuty === 0 && childCount > 0) {
    breachTypes.push("unqualified_unsupervised");
    status = "breached";
  }

  if (eytLost) {
    breachTypes.push("eyt_lost");
  }

  // PFA coverage: at least one on-duty staff with valid PFA when children are signed in
  if (childCount > 0) {
    const hasPfa = zoneShifts.some((s) => {
      const st = byId.get(s.staff_id);
      if (!st?.paediatric_first_aid) return false;
      if (!st.first_aid_expiry) return false;
      return new Date(st.first_aid_expiry).getTime() >= now.getTime();
    });
    if (!hasPfa) {
      breachTypes.push("pfa_lost");
      if (status !== "breached") status = "approaching";
    }
  }

  if (childCount > 0 && countedStaff < requiredAdults) {
    breachTypes.push("ratio");
    status = "breached";
  } else if (childCount > 0) {
    // Approaching: 1-2 more children would push us over the line
    const headroom = countedStaff * denom - childCount;
    if (status !== "breached" && headroom >= 0 && headroom <= 2) {
      status = "approaching";
    }
  }

  const currentRatioLabel =
    countedStaff > 0 && childCount > 0
      ? `1:${(childCount / countedStaff).toFixed(1)}`
      : countedStaff > 0
      ? "1:0"
      : "N/A";

  const unassignedNote =
    unassigned > 0
      ? ` ${unassigned} staff member${unassigned === 1 ? " is" : "s are"} signed in at the Kiosk with no room set, so they are not counted here. Set their room on today's rota if they are working in ${zone.name}.`
      : "";

  let message = "";
  if (breachTypes.includes("ratio")) {
    const need = Math.max(0, requiredAdults - countedStaff);
    message = `Ratio breach: ${zone.name}. ${childCount} child${childCount === 1 ? "" : "ren"} signed in, ${countedStaff} staff present, ${legalRatio} needs ${requiredAdults}. ${need} more adult${need === 1 ? "" : "s"} needed.${unassignedNote}`;
  } else if (breachTypes.includes("unqualified_unsupervised")) {
    message = `No Level 3 qualified staff present in ${zone.name}. Staff numbers meet the ${legalRatio} ratio, but unqualified staff should not be left without a qualified colleague.`;
  } else if (breachTypes.includes("eyt_lost")) {
    message = `EYT/EYPS/QTS leader no longer on duty in ${zone.name}. Standard 1:8 ratio now applies.`;
  } else if (breachTypes.includes("pfa_lost")) {
    message = `No Paediatric First Aid trained staff on duty in ${zone.name}.`;
  } else if (status === "approaching") {
    message = `${zone.name} is approaching its legal ratio limit.`;
  }

  return {
    zoneId,
    zoneName: zone.name,
    ageGroup,
    isEytLed,
    legalRatio,
    requiredAdults,
    countedStaff,
    qualifiedOnDuty,
    unqualifiedOnDuty,
    signedInChildren: childCount,
    currentRatioLabel,
    status,
    breachTypes,
    message,
    snapshot: {
      computed_at: now.toISOString(),
      age_group: ageGroup,
      legal_ratio: legalRatio,
      children: signedInChildren.map((c) => ({ id: c.id, dob: c.dob, age_band: ageGroupForChild(c.dob, now) })),
      staff_counted: zoneShifts.map((s: any) => ({
        staff_id: s.staff_id,
        shift_id: s.id,
        planned: `${s.start_time?.slice(0, 5)}-${s.end_time?.slice(0, 5)}`,
        kiosk_in: s.actual_start_time ?? null,
        kiosk_out: s.actual_end_time ?? null,
        l3: byId.get(s.staff_id)?.is_qualified_l3 ?? null,
      })),
      staff_signed_in_no_room: unassigned,
    },
  };
}

/** Fallback age group inference from existing zone IDs when not configured. */
export function inferAgeGroup(zoneId: string): AgeGroup {
  if (zoneId === "baby") return "under_2";
  if (zoneId === "toddler") return "two_year_olds";
  if (zoneId === "preschool") return "three_to_five";
  if (zoneId === "out_of_school") return "out_of_school";
  return "mixed";
}
type PresenceShift = DbShift & {
  date: string;
  start_time: string;
  end_time: string;
  actual_start_time?: string | null;
  actual_end_time?: string | null;
};

/**
 * Is this person actually present right now? Kiosk sign-in/out wins over the
 * planned rota times:
 *  - signed out already            -> not present
 *  - signed in and not signed out  -> present, even if not on the rota
 *    (ad-hoc Kiosk sign-ins are stored as zero-length shifts) or past the
 *    planned finish
 *  - no Kiosk record               -> planned rota window
 */
export function isShiftPresentNow(s: PresenceShift, hhmm: string): boolean {
  const aStart = s.actual_start_time ? s.actual_start_time.slice(0, 5) : null;
  const aEnd = s.actual_end_time ? s.actual_end_time.slice(0, 5) : null;
  if (aEnd && aEnd <= hhmm) return false;
  if (aStart && !aEnd) return aStart <= hhmm;
  return s.start_time.slice(0, 5) <= hhmm && s.end_time.slice(0, 5) > hhmm;
}

/** Returns shifts whose staff member is present at the given time on its date. */
export function filterActiveShifts<T extends PresenceShift>(shifts: T[], now = new Date()): T[] {
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return shifts.filter((s) => s.date === dateStr && isShiftPresentNow(s, hhmm));
}

export const DUTY_STATUS_LABELS: Record<DutyStatus, string> = {
  on_duty: "On duty",
  on_break: "On break",
  in_meeting: "In meeting",
  admin_duties: "Admin duties",
};

export const AGE_GROUP_LABELS: Record<AgeGroup, string> = {
  under_2: "Under 2 (1:3)",
  two_year_olds: "2-year-olds (1:4)",
  three_to_five: "3-5 year olds (1:8, or 1:13 with EYT/EYPS/QTS lead)",
  mixed: "Mixed ages (most restrictive applies)",
  out_of_school: "Out of school (1:8)",
};
